import React, {forwardRef, PropsWithChildren, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Renderer} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface NavigationHeaderProps {
  children: ReactNode;
  onToggleExpandClick?: () => void;
  hasHover?: boolean;
  isExpanded?: boolean;
  more?: ReactNode;
  renderTooltip?: Renderer;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: grid;
  grid-template-areas: 'title . more';
  grid-template-columns: 1fr 4px auto;
  align-items: center;

  padding-right: 8px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledTitleButton = styled(ChromelessButton)`
  grid-area: title;
  display: flex;
  align-items: center;
  line-height: normal;
  gap: 3px;

  padding: 6px 8px 6px 14px;
  min-width: 0;
`;

interface ChevronStyleProps {
  $isExpanded: boolean;
}

const StyledChevronDiv = styled.div<ChevronStyleProps>`
  transform: translateY(0.5px);
  svg {
    transform: scaleY(${(p) => (p.$isExpanded ? '-1' : '1')});
  }
`;

interface LabelStyleProps {
  $hasHover: boolean;
}

const StyledLabelDiv = styled.div<LabelStyleProps>`
  ${ellipsis()};

  line-height: 16px;
  color: ${(p) => p.theme.leftBar.header};
  ${hoverSelector} {
    color: ${(p) => (p.$hasHover ? p.theme.leftBar.headerHover : p.theme.leftBar.header)};
  }
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: none;

  ${hoverSelector} {
    display: block;
  }
`;

/*
 * Component.
 */

export const NavigationHeader = forwardRef<HTMLDivElement, PropsWithChildren<NavigationHeaderProps>>(
  (props, ref) => {
    const {children, more, onToggleExpandClick, renderTooltip} = props;
    const theme = useUserTheme();
    const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

    const hasHover = props.hasHover || tooltipIsOpen;

    return (
      <StyledHeaderDiv ref={ref} className={buildHoverParentClassName(hasHover)}>
        <StyledTitleButton onClick={onToggleExpandClick}>
          <StyledLabelDiv $hasHover={Boolean(onToggleExpandClick)}>{children}</StyledLabelDiv>
          {maybeRenderChevron(props, theme)}
          {props.renderTooltip && (
            <StyledTooltipCoordinator
              isInline
              persistent
              placement="right-start"
              onShow={() => setTooltipIsOpen(true)}
              onHide={() => setTooltipIsOpen(false)}
              render={renderTooltip}
              borderRadius={16}
            />
          )}
        </StyledTitleButton>
        {more}
      </StyledHeaderDiv>
    );
  },
);

function maybeRenderChevron(props: NavigationHeaderProps, theme: UserTheme) {
  const {onToggleExpandClick, isExpanded} = props;

  if (!onToggleExpandClick) {
    return null;
  }

  return (
    <StyledChevronDiv $isExpanded={Boolean(isExpanded)}>
      <Icon name="chevronExpandTiny" color={theme.leftBar.header} />
    </StyledChevronDiv>
  );
}
